/**
 * Site-Wide Jackpot OptInManager service for checking user opt-in status.
 *
 * This module handles the initial check of user opt-in status when the app loads
 * using the OptInManager from @fanduel/casino-swj-consumer.
 */

import { ddRumError } from '@/utils/datadog';
import { ddErrorSwjCheckOptinStatus } from '@/utils/datadog/site-wide-jackpot';
import { optInManagerService } from '@/app/components/site-wide-jackpot/hooks/use-opt-in-manager';

type OptInCallbacks = {
    onOptInStatusChange: (multiplier: number) => void;
    onHasInitialized: (multiplier: number) => void;
    onError: (multiplier: number) => void;
};

/**
 * Checks the user's site-wide jackpot opt-in status on app initialization.
 *
 * @param gthBaseUrl - The GTH (Game Transaction Hub) base URL for the current region
 * @param jackpotId - The provider jackpot ID
 * @param callbacks - Callback functions to update Zustand store state
 */
export const checkSiteWideJackpotsUserOptInStatus = async (
    gthBaseUrl: string,
    jackpotId: string,
    { onOptInStatusChange, onHasInitialized, onError }: OptInCallbacks
): Promise<void> => {
    const handleError = (error: unknown) => {
        ddRumError(ddErrorSwjCheckOptinStatus(error as Error, { jackpotId }));
    };

    const dispatchIsOptedIn = (value: number | { [key: string]: boolean } | null | undefined) => {
        if (value === null || value === undefined) {
            return;
        }

        const numericValue = typeof value === 'number' ? value : 0;
        onOptInStatusChange(numericValue);
        onHasInitialized(numericValue);
    };

    // Configure the singleton OptInManager instance with endpoint, origin, and jackpotId
    // This ensures the same instance is used by JackpotMultiplierPopup
    optInManagerService.configure(gthBaseUrl, 'lobby', jackpotId, {
        callback: dispatchIsOptedIn,
        errorCallback: handleError
    });

    try {
        await optInManagerService.getInstance().checkOptedIn(jackpotId);
    } catch (error) {
        console.warn('Error checking site wide jackpots user opt in status', error);
        handleError(error);
        onError(0);
    }
};
